/**
 * FieldScoutModePage — route entry point for the new Field Scout recording flow.
 *
 * Three phases managed here:
 *   'selection'  → orchard + scouter selection form
 *   'recording'  → <FieldScoutRecording> with a real useFieldScoutRecording hook
 *   'review'     → <FieldScoutReview> summary + submit
 *
 * Desktop gate: if !isCompactDevice(), render a "use handheld" notice instead of
 * the recorder. Keep this gate centralised here so sub-components stay pure.
 *
 * Track: field_scout_recording_mobile / Slice 2 / Task S2-11.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db, type Orchard, type Pest } from '../../../db';
import { isCompactDevice } from '../../../utils/platform';
import { pct, examinedCount } from '../../../scouting/recording';
import { normalizeMeasurementType, typeDefaultThreshold } from '../../../scouting/pestConfig';
import { useFieldScoutRecording } from './useFieldScoutRecording';
import { FieldScoutRecording } from './FieldScoutRecording';
import { FieldScoutReview, type ReviewRow } from './FieldScoutReview';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDate } from '../../../dateFormat';
import './FieldScoutRecording.css';

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function pageActionThreshold(p: Pest): number {
  return typeof p.actionThreshold === 'number'
    ? p.actionThreshold
    : typeDefaultThreshold(normalizeMeasurementType(p.measurementType));
}

type Phase = 'selection' | 'recording' | 'review';

// ─────────────────────────────────────────────────────────────────────────────
//  Inner recording view — instantiates the hook
// ─────────────────────────────────────────────────────────────────────────────

interface RecordingViewProps {
  orchardId: string;
  scouterName: string;
  orchardName: string;
  cultivar?: string;
  pests: Pest[];
  onFinish: () => void;
  onReset: () => void;
}

const RecordingView: React.FC<RecordingViewProps> = ({
  orchardId,
  scouterName,
  orchardName,
  cultivar,
  pests,
  onFinish,
  onReset,
}) => {
  const { t } = useTranslation();
  const rec = useFieldScoutRecording(orchardId, scouterName, orchardName, pests);

  if (rec.loading) {
    return (
      <div className="fsr-loading" data-testid="fsr-loading">
        {t('fieldScout.recording.start')}…
      </div>
    );
  }

  return (
    <FieldScoutRecording
      rec={rec}
      pests={pests}
      orchardName={orchardName}
      cultivar={cultivar}
      onFinish={onFinish}
      onCancel={async () => {
        await rec.discard();
        onReset();
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Inner review view — instantiates the hook (same orchardId/scouterName)
// ─────────────────────────────────────────────────────────────────────────────

interface ReviewViewProps {
  orchardId: string;
  scouterName: string;
  orchardName: string;
  cultivar?: string;
  pests: Pest[];
  onSubmitDone: () => void;
}

const ReviewView: React.FC<ReviewViewProps> = ({
  orchardId,
  scouterName,
  orchardName,
  cultivar,
  pests,
  onSubmitDone,
}) => {
  const { t } = useTranslation();
  const rec = useFieldScoutRecording(orchardId, scouterName, orchardName, pests);
  const [submitting, setSubmitting] = useState(false);

  // Build review rows from draft state
  const examined = examinedCount(rec.maxExamined);
  const today = formatDate(new Date());

  const rows: ReviewRow[] = [];

  for (const p of pests) {
    const mt = normalizeMeasurementType(p.measurementType);
    const threshold = pageActionThreshold(p);

    if (mt === 'incidence') {
      const infestedCount = rec.infested[p.id]?.length ?? 0;
      if (infestedCount > 0) {
        const value = pct(infestedCount, examined);
        rows.push({
          code: p.code,
          name: p.name,
          type: 'incidence',
          value,
          threshold,
          display: `${value}%`,
        });
      }
    } else if (mt === 'level') {
      const severity = rec.levels[p.id] ?? 0;
      if (severity > 0) {
        rows.push({
          code: p.code,
          name: p.name,
          type: 'level',
          value: severity,
          threshold,
          display: `${severity}/4`,
        });
      }
    } else if (mt === 'count') {
      const value = rec.counts[p.id] ?? 0;
      if (value > 0) {
        rows.push({
          code: p.code,
          name: p.name,
          type: 'count',
          value,
          threshold,
          display: `${value}`,
        });
      }
    }
  }

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await rec.submit();
      onSubmitDone();
    } catch (err) {
      console.error('[FieldScoutModePage] submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (rec.loading) {
    return <div className="fsr-loading">{t('fieldScout.recording.start')}…</div>;
  }

  return (
    <FieldScoutReview
      blockName={orchardName}
      cultivar={cultivar}
      examined={examined}
      date={today}
      rows={rows}
      onSubmit={handleSubmit}
      submitting={submitting}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Selection form
// ─────────────────────────────────────────────────────────────────────────────

interface SelectionFormProps {
  onStart: (orchardId: string, orchardName: string, cultivar: string | undefined, scouterName: string, pests: Pest[]) => void;
}

const SelectionForm: React.FC<SelectionFormProps> = ({ onStart }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [allPests, setAllPests] = useState<Pest[]>([]);
  const [orchardId, setOrchardId] = useState('');
  const [scouterName, setScouterName] = useState(user?.name ?? '');
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([db.orchards.toArray(), db.pests.toArray()]).then(([os, ps]) => {
      setOrchards(
        [...os].sort((a, b) =>
          a.orchardName.localeCompare(b.orchardName, undefined, { numeric: true, sensitivity: 'base' }),
        ),
      );
      setAllPests([...ps].sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name)));
    });
  }, []);

  const handleStart = () => {
    if (!orchardId) { setError(t('fieldScout.recording.selectOrchard')); return; }
    if (!scouterName.trim()) { setError(t('fieldScout.recording.scouterName')); return; }

    const orchard = orchards.find((o) => o.id === orchardId);
    if (!orchard) return;

    // Filter to enabled (all for now — no per-crop filtering yet; extend later)
    const pests = allPests.filter((p) => p.code && p.name);

    onStart(orchardId, orchard.orchardName, orchard.cultivar ?? orchard.variety, scouterName.trim(), pests);
  };

  return (
    <div className="fsr-selection" data-testid="fsr-selection">
      <div className="fsr-selection__card">
        <h2 className="fsr-selection__title">
          {t('fieldScout.recording.start')}
        </h2>

        {error && (
          <div className="fsr-selection__error" role="alert">{error}</div>
        )}

        <label className="fsr-selection__label">
          {t('fieldScout.recording.selectOrchard')}
          <select
            className="fsr-selection__input"
            value={orchardId}
            onChange={(e) => { setOrchardId(e.target.value); setError(''); }}
            data-testid="select-orchard"
          >
            <option value="">—</option>
            {orchards.map((o) => (
              <option key={o.id} value={o.id}>{o.orchardName}</option>
            ))}
          </select>
        </label>

        <label className="fsr-selection__label">
          {t('fieldScout.recording.scouterName')}
          <input
            type="text"
            className="fsr-selection__input"
            value={scouterName}
            onChange={(e) => { setScouterName(e.target.value); setError(''); }}
            data-testid="input-scouter"
          />
        </label>

        <button
          type="button"
          className="fsr-selection__btn"
          onClick={handleStart}
          data-testid="btn-start"
        >
          {t('fieldScout.recording.start')}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Desktop notice
// ─────────────────────────────────────────────────────────────────────────────

const DesktopNotice: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="fsr-desktop-notice" data-testid="fsr-desktop-notice">
      <p>{t('fieldScout.recording.desktopNotice', 'Open this page on a handheld device to start recording.')}</p>
      <Link to="/scouting/manual-v2" className="fsr-desktop-notice__link" data-testid="link-manual-v2">
        {t('fieldScout.desktop.openManualCapture')}
      </Link>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Page root
// ─────────────────────────────────────────────────────────────────────────────

const FieldScoutModePage: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('selection');
  const [orchardId, setOrchardId] = useState('');
  const [orchardName, setOrchardName] = useState('');
  const [cultivar, setCultivar] = useState<string | undefined>();
  const [scouterName, setScouterName] = useState('');
  const [pests, setPests] = useState<Pest[]>([]);

  // Desktop gate — rendered once per mount
  const isHandheld = isCompactDevice();

  const handleStart = (
    oid: string,
    oName: string,
    cult: string | undefined,
    scouter: string,
    selectedPests: Pest[],
  ) => {
    setOrchardId(oid);
    setOrchardName(oName);
    setCultivar(cult);
    setScouterName(scouter);
    setPests(selectedPests);
    setPhase('recording');
  };

  const handleSubmitDone = () => {
    // Reset to selection so the next scout can start fresh
    setPhase('selection');
    setOrchardId('');
    setOrchardName('');
    setCultivar(undefined);
    setScouterName('');
    setPests([]);
  };

  if (!isHandheld) {
    return <DesktopNotice />;
  }

  if (phase === 'selection') {
    return <SelectionForm onStart={handleStart} />;
  }

  if (phase === 'recording') {
    return (
      <RecordingView
        orchardId={orchardId}
        scouterName={scouterName}
        orchardName={orchardName}
        cultivar={cultivar}
        pests={pests}
        onFinish={() => setPhase('review')}
        onReset={() => setPhase('selection')}
      />
    );
  }

  // phase === 'review'
  return (
    <ReviewView
      orchardId={orchardId}
      scouterName={scouterName}
      orchardName={orchardName}
      cultivar={cultivar}
      pests={pests}
      onSubmitDone={handleSubmitDone}
    />
  );
};

export default FieldScoutModePage;
